import type { UpdateParams, GetListParams, GetOneParams, CreateParams } from 'react-admin';
import axiosApiInstance, { postRequestCacheConfig } from '../../axiosConfig';
import ApiMocks from 'src/api/mocks';
import { childToAPITransform } from 'src/api/Children/transforms';
import { Resource } from 'src/constants/resources';
import { Child } from 'src/components/Children/types';
import type { ChildVisit } from 'src/components/Children/types';
import { mapEducationLevelToGroupFull } from 'src/utils/children';
import { ChildrenFilterManager } from 'src/managers/Children/ChildrenFilterManager';

export default {
    getList: async (params: GetListParams) => {
        let { keyword, ...filters } = params.filter;
        filters = Object.keys(filters).length ? filters : undefined;
        ChildrenFilterManager.defineFilters(filters);

        const pageRequest = {
            page: params.pagination.page - 1,
            size: params.pagination.perPage,
            sort: params.sort && { properties: [params.sort.field], direction: params.sort.order },
            keyword,
            filters,
            _timestamp: Date.now()
        };

        const response = await axiosApiInstance.post(Resource.CHILDREN, pageRequest, {
            ...postRequestCacheConfig,
            headers: {
                ...postRequestCacheConfig.headers,
                'Cache-Control': 'no-cache'
            }
        });

        // as backend will not transform classes into levels - making it happen on a UI side
        const childrenList = response.data.content as Child[];
        childrenGradeToLevelUpdater(childrenList);

        return {
            data: childrenList,
            total: response.data.totalElements
        };
    },

    create: (params: CreateParams) => {
        const { method } = params.meta || {};
        const methodUrlPath = method ? `${method.startsWith('/') ? '' : '/'}${method}` : '/add';
        const child = params.data as Child;
        Child.validate(child);

        return axiosApiInstance.post(`${Resource.CHILDREN}${methodUrlPath}`, child).then((response) => response);
    },

    update: (params: UpdateParams) => {
        const { useMock, method } = params.meta || {};
        if (useMock && ApiMocks[Resource.CHILDREN].update) {
            return Promise.resolve({
                data: { ...ApiMocks[Resource.CHILDREN].getOne, id: params.id }
            });
        }
        const methodUrlPath = method ? `${method.startsWith('/') ? '' : '/'}${method}` : '/update';
        const child = params.data as Child;
        Child.validate(child);

        return axiosApiInstance
            .put(`${Resource.CHILDREN}${methodUrlPath}`, childToAPITransform(child))
            .then((response) => response);
    },

    getOne: (params: GetOneParams) => {
        return axiosApiInstance.get(`${Resource.CHILDREN}/${params.id}`).then((response) => {
            // as backend will not transform classes into levels - making it happen on a UI side
            response.data.visit.educationLevelGroup = mapEducationLevelToGroupFull(response.data.visit.educationLevel);
            childrenVisitHistoryGradeToLevelUpdater(response.data.visitHistory);

            return response;
        });
    }
};

/**
 * Updates the response, by adding educationLevelGroup property based on educationLevel
 * @param { Child[] } childrenData
 */
const childrenGradeToLevelUpdater = (childrenData: Child[]): void => {
    childrenData?.forEach((child: Child) => {
        child.visit.educationLevelGroup = mapEducationLevelToGroupFull(child.visit.educationLevel);
    });
};

/**
 * Updates the response, by adding educationLevelGroup property based on educationLevel for the visitHistory array
 * @param { ChildVisit[] } visitHistory
 */
const childrenVisitHistoryGradeToLevelUpdater = (visitHistory: ChildVisit[]): void => {
    visitHistory?.forEach((visit: ChildVisit) => {
        visit.educationLevelGroup = mapEducationLevelToGroupFull(visit.educationLevel);
    });
};
